import React, { useEffect, useMemo, useRef } from 'react';
import {
    Timer,
    formatTimerValue,
    useAnalytics,
    useAuthManager,
    Offer,
    PaymentPlace,
    TarifficatorPurchaseSuccessWidgetEvent,
} from '@funtech-int/yango-shared';
import classnames from 'classnames';

import { getAutoOpenWidgetUrl } from '@/shared/lib/getAutoOpenWidgetUrl';
import { clientConfig } from '@/config/client';
import { Button } from '@/shared/components/Button/Button';
import { useLoginUrl } from '@/shared/hooks/useLoginUrl';
import { useOfferWebAnalytics } from '@/shared/hooks/useOfferWebAnalytics';
import { useSubscriptionRefetch } from '@/shared/hooks/useSubscriptionRefetch';
import { usePaymentWidgetModal } from '@/shared/hooks/usePopups';
import { InterpolatedOfferText } from '@/shared/components/InterpolatedOfferText/InterpolatedOfferText';

import { getSubscriptionOfferButtonText } from '../../lib/getSubscriptionOfferButtonText';
import type { CounterOfferAnalytics } from '../../lib/counterOfferAnalytics';
import { useI18n } from '../../hooks/useOnboardingI18n';
import { createOfferAnalytics } from '../../lib/offerAnalytics';

import styles from './CounterOfferModal.module.css';

const {
    defaultMediaBillingConfig: { target: DEFAULT_MEDIA_BILLING_TARGET },
} = clientConfig;

interface CounterOfferModalProps {
    counterOffer: Offer;
    baseOffer: Offer | null;
    paymentPlace: PaymentPlace;
    onClose: VoidFunction;
    counterOfferAnalytics: CounterOfferAnalytics;
}

export const CounterOfferModal = ({
    counterOffer,
    baseOffer,
    paymentPlace,
    onClose,
    counterOfferAnalytics,
}: CounterOfferModalProps) => {
    const { isAuthorized } = useAuthManager();
    const i18n = useI18n();
    const { refetch: subscriptionRefetch } = useSubscriptionRefetch();
    const offerWebAnalytics = useOfferWebAnalytics(counterOffer);

    const {
        customTexts,
        buttonTextWithDetails,
        buttonText: offerButtonText,
    } = counterOffer.offerTexts;

    const oldPriceText = baseOffer?.offerTexts?.customTexts?.comparedPrice;
    const timerDurationMs = Number(customTexts?.timerDuration)
        ? Number(customTexts?.timerDuration) * 60 * 1000
        : undefined;

    const remainingMsRef = useRef(timerDurationMs ?? 0);

    const buttonText = getSubscriptionOfferButtonText(i18n, offerButtonText, !isAuthorized);

    const offerAnalytics = useAnalytics(createOfferAnalytics);
    offerAnalytics.setData({
        buttonText,
        offer: counterOffer,
    });

    const target = counterOffer.target ?? DEFAULT_MEDIA_BILLING_TARGET;
    const offerLoginUrl = useLoginUrl(getAutoOpenWidgetUrl(counterOffer));
    const paymentWidget = usePaymentWidgetModal();

    const controller = useMemo(() => {
        function onPurchaseSuccess(data: TarifficatorPurchaseSuccessWidgetEvent) {
            subscriptionRefetch();
            offerWebAnalytics.purchaseSucceeded(data);

            if (counterOffer) {
                offerAnalytics.succeeded();
            }
        }

        function onSuccessClose() {
            if (!counterOffer) return;

            onClose?.();
        }

        return {
            onOfferShow: () => {
                if (!counterOffer) return;

                offerWebAnalytics.offerShowed();
                paymentPlace.analytics.offerShowed(counterOffer);
                offerAnalytics.showed();
            },
            onOfferClick: () => {
                if (!counterOffer) return;

                paymentPlace.analytics.offerClicked(counterOffer);
                offerAnalytics.navigated();
                offerWebAnalytics.offerClicked();
                counterOfferAnalytics.subscribeButtonNavigated(
                    formatTimerValue(remainingMsRef.current)
                );

                paymentWidget.open(paymentPlace, counterOffer, {
                    onPurchaseSuccess,
                    onSuccessClose,
                    onNeedAuth: () => window.location.assign(offerLoginUrl),
                });
            },
        };
    }, [
        offerAnalytics,
        counterOffer,
        counterOfferAnalytics,
        offerLoginUrl,
        offerWebAnalytics,
        onClose,
        paymentWidget,
        paymentPlace,
        subscriptionRefetch,
    ]);

    useEffect(() => {
        counterOfferAnalytics.showed();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { handleTimerTick, handleTimerExpire } = useMemo(
        () => ({
            handleTimerTick(ms: number) {
                remainingMsRef.current = ms;
            },
            handleTimerExpire() {
                counterOfferAnalytics.timeOutOverLimit();
                onClose();
            },
        }),
        [counterOfferAnalytics, onClose]
    );

    if (!target) {
        return null;
    }

    const shouldShowAdditionText = buttonTextWithDetails && isAuthorized;
    const buttonVariant = isAuthorized ? 'plus' : 'accent';
    const {
        plusLogo: logoUrl,
        paywallTitle: title,
        comparedPrice: priceText,
        additionalText: subtitle,
        desktopBackgroundUrl: backgroundUrl,
    } = customTexts || {};

    return (
        <div
            className={styles.root}
            style={backgroundUrl ? { backgroundImage: `url(${backgroundUrl})` } : undefined}
        >
            {logoUrl && <img className={styles.logo} src={logoUrl} alt="Yango Plus" />}

            <div className={styles.container}>
                <div className={styles.titleBlock}>
                    {timerDurationMs && (
                        <Timer
                            className={styles.timer}
                            durationMs={timerDurationMs}
                            onTick={handleTimerTick}
                            onExpire={handleTimerExpire}
                        />
                    )}

                    {title && <h2 className={styles.title}>{title}</h2>}

                    <div className={styles.prices}>
                        {priceText && <span className={styles.price}>{priceText}</span>}
                        {oldPriceText && <span className={styles.oldPrice}>{oldPriceText}</span>}
                    </div>
                </div>

                <div className={styles.offerBlock}>
                    <Button
                        className={classnames(styles.button, styles.ctaButton)}
                        size={48}
                        variant={buttonVariant}
                        onClick={controller.onOfferClick}
                        data-test-id="SubscriptionPurchaseButton"
                        onShow={controller.onOfferShow}
                    >
                        {buttonText}
                    </Button>

                    {shouldShowAdditionText && (
                        <InterpolatedOfferText
                            legals={counterOffer.catalogCompositeOffer?.legalInfo?.items}
                            text={buttonTextWithDetails}
                            className={styles.additionText}
                        />
                    )}
                    {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
                </div>
            </div>
        </div>
    );
};
